import * as z from "zod";
import { isoDate } from "./common";

export const STORE_STATUSES = ["ACTIVE", "HIDDEN", "SUSPENDED"] as const;
export type StoreStatus = (typeof STORE_STATUSES)[number];

export const CONNECTION_KINDS = ["COMMERCE", "PAYMENT", "RETAIL_SYNC"] as const;
export type ConnectionKind = (typeof CONNECTION_KINDS)[number];

export const CONNECTION_STATUSES = ["PENDING", "ACTIVE", "DISCONNECTED", "ERROR"] as const;
export type ConnectionStatus = (typeof CONNECTION_STATUSES)[number];

export const StoreConnection = z.object({
  id: z.string(),
  providerKey: z.string(),
  kind: z.enum(CONNECTION_KINDS),
  status: z.enum(CONNECTION_STATUSES),
  externalAccountRef: z.string().nullable(),
  lastError: z.string().nullable(),
  lastSyncedAt: isoDate.nullable(),
  createdAt: isoDate,
  updatedAt: isoDate,
});
export type StoreConnection = z.output<typeof StoreConnection>;

export const SHOP_CAPABILITIES = ["POINT_OF_SALE", "LIVE_BREAKS", "HOST_EVENTS"] as const;
export type ShopCapability = (typeof SHOP_CAPABILITIES)[number];

/** What staff have switched on for the shop, and what it has asked for and is waiting on. */
export const StoreCapabilities = z.object({
  /** The counter: customer scans and carts, taking payment, the Shopify POS extension. */
  pointOfSale: z.boolean(),
  /** Live streams, breaks and their shipping. */
  liveBreaks: z.boolean(),
  /** Hosts card shows and sells their tables. */
  hostEvents: z.boolean(),
  requested: z.array(z.enum(SHOP_CAPABILITIES)),
  /** Turned down by staff, with the reason to show. Asking again reopens the request. */
  declined: z.array(
    z.object({
      capability: z.enum(SHOP_CAPABILITIES),
      reason: z.string().nullable(),
      declinedAt: isoDate.nullable(),
    }),
  ),
  /** Where HobbyScan answers: the shop's contact email, else the last request's. Null means a request must give one. */
  contactEmail: z.string().nullable(),
});
export type StoreCapabilities = z.output<typeof StoreCapabilities>;

export const STORE_ROLES = ["OWNER", "MANAGER", "STAFF"] as const;
export type StoreRole = (typeof STORE_ROLES)[number];

export const Store = z.object({
  id: z.string(),
  status: z.enum(STORE_STATUSES),
  name: z.string().nullable(),
  description: z.string().nullable(),
  logoUrl: z.string().nullable(),
  currencyCode: z.string().nullable(),
  quoteValidityMinutes: z.number(),
  websiteUrl: z.string().nullable(),
  slug: z.string().nullable(),
  contactEmail: z.string().nullable(),
  returnsPolicy: z.string().nullable(),
  createdAt: isoDate,
  updatedAt: isoDate,
  connections: z.array(StoreConnection),
  /** The caller's role in this shop; a Shopify session is OWNER. */
  myRole: z.enum(STORE_ROLES),
});
export type Store = z.output<typeof Store>;

export interface NewStore {
  name: string;
  description?: string;
  logoUrl?: string;
  /** Three-letter uppercase ISO code. */
  currencyCode?: string;
}

/** Partial profile update. `null` clears a field; an omitted one is left alone. */
export interface StoreUpdate {
  name?: string;
  description?: string | null;
  logoUrl?: string | null;
  currencyCode?: string | null;
  /** The merchant's own site; null switches them to the hosted shop page. */
  websiteUrl?: string | null;
  contactEmail?: string | null;
  returnsPolicy?: string | null;
}

export const StoreAdmin = z.object({
  id: z.string(),
  name: z.string(),
  email: z.string(),
  role: z.enum(STORE_ROLES),
  linkedAt: isoDate,
  /** The caller. Always false under a Shopify session, which is the shop rather than a person. */
  isSelf: z.boolean(),
});
export type StoreAdmin = z.output<typeof StoreAdmin>;

export const MERCHANT_ACTIONS = ["ONBOARDING", "WAIT", "SUPPORT", "NONE"] as const;
export type MerchantAction = (typeof MERCHANT_ACTIONS)[number];

/** A connection plus what the merchant can do about one that is not ACTIVE. Null for a provider that publishes no such state. */
export const StoreProvider = StoreConnection.extend({
  merchantAction: z.enum(MERCHANT_ACTIONS).nullable(),
});
export type StoreProvider = z.output<typeof StoreProvider>;

export const StoreProviders = z.object({
  providers: z.array(StoreProvider),
  /** In test mode an existing connected account can be attached by id (`StripeApi.attach`). */
  stripeMode: z.enum(["live", "test"]),
});
export type StoreProviders = z.output<typeof StoreProviders>;

export const StoreProviderStatus = z.object({
  providerKey: z.string(),
  kind: z.enum(CONNECTION_KINDS),
  /** Null when the store has never connected this provider. */
  connection: StoreConnection.nullable(),
});
export type StoreProviderStatus = z.output<typeof StoreProviderStatus>;

export const LOCATION_KINDS = ["SHOP", "BOOTH", "WAREHOUSE"] as const;
export type LocationKind = (typeof LOCATION_KINDS)[number];

/** The show a booth stands at. */
export const LocationEvent = z.object({
  id: z.string(),
  title: z.string(),
  startsAt: isoDate,
  endsAt: isoDate,
  /** The venue's IANA zone; null when the source gave none. */
  timezone: z.string().nullable(),
});
export type LocationEvent = z.output<typeof LocationEvent>;

export const Location = z.object({
  id: z.string(),
  storeId: z.string(),
  name: z.string(),
  address1: z.string().nullable(),
  address2: z.string().nullable(),
  city: z.string().nullable(),
  province: z.string().nullable(),
  zip: z.string().nullable(),
  country: z.string().nullable(),
  countryCode: z.string().nullable(),
  phone: z.string().nullable(),
  openingHours: z.string().nullable(),
  latitude: z.number().nullable(),
  longitude: z.number().nullable(),
  isActive: z.boolean(),
  /** SHOP is the shop's own premises; BOOTH a table at a show, for the show's dates. */
  kind: z.enum(LOCATION_KINDS),
  /** The show a booth stands at; null for anything else. */
  event: LocationEvent.nullable(),
  /** The Stripe Terminal location this branch is registered as; null until it is set up for in-person payments. */
  stripeTerminalLocationId: z.string().nullable(),
  createdAt: isoDate,
  updatedAt: isoDate,
});
export type Location = z.output<typeof Location>;

/** Partial edit of a branch. Validated against the address it leaves behind, so a field cannot be emptied. */
export interface LocationUpdate {
  name?: string;
  address1?: string;
  address2?: string;
  city?: string;
  province?: string;
  zip?: string;
  countryCode?: string;
  phone?: string;
  openingHours?: string;
}

export const ShipFromOption = z.object({
  locationId: z.string(),
  /** Address fields still needed before a label can leave from here. Empty means usable. */
  missing: z.array(z.string()),
  /** Why a complete address still cannot ship (outside the US). Null when usable. */
  blocked: z.string().nullable(),
});
export type ShipFromOption = z.output<typeof ShipFromOption>;

export const ShipFrom = z.object({
  /** The merchant's explicit choice, or null. */
  selectedLocationId: z.string().nullable(),
  /** What a label would use today: the selection when usable, else a store's only location, else null. */
  resolvedLocationId: z.string().nullable(),
  options: z.array(ShipFromOption),
});
export type ShipFrom = z.output<typeof ShipFrom>;

export const StoreLocations = z.object({
  locations: z.array(Location),
  shipFrom: ShipFrom,
});
export type StoreLocations = z.output<typeof StoreLocations>;

export const ShopPage = z.object({
  slug: z.string(),
  /** Built server-side; never assemble it on the client. */
  url: z.string(),
});
export type ShopPage = z.output<typeof ShopPage>;

export const StoreTaxSettings = z.object({
  /** On by default, so false always means the merchant switched it off. */
  collectSalesTax: z.boolean(),
  accountConnected: z.boolean(),
  /** "active", "pending", or null if never read. */
  taxStatus: z.string().nullable(),
  hasSourceAddress: z.boolean(),
  /** "US-MO" style; null without a usable address. */
  sourceJurisdiction: z.string().nullable(),
  sourceCovered: z.boolean(),
  sourceLocationName: z.string().nullable(),
  registrations: z.array(z.string()),
  otherLocationJurisdictions: z.array(z.string()),
  registrationsUrl: z.string(),
  /** The connected account the state was read from; null without a connection. */
  accountId: z.string().nullable(),
  stripeMode: z.enum(["live", "test"]),
  /** Null when Stripe answered; otherwise why not, and the Stripe fields are the cached values. */
  stripeReadError: z.string().nullable(),
  /** The account's default product tax code in Stripe; null when unset or unreadable. */
  defaultTaxCodeId: z.string().nullable(),
  defaultTaxCodeName: z.string().nullable(),
  /** What every card line is actually sent as, whatever the default says. */
  cardTaxCodeId: z.string(),
  cardTaxCodeName: z.string(),
});
export type StoreTaxSettings = z.output<typeof StoreTaxSettings>;

export interface NewLocation {
  name: string;
  address1: string;
  address2?: string;
  city: string;
  province?: string;
  zip?: string;
  /** Two-letter ISO code. */
  countryCode: string;
  phone?: string;
  openingHours?: string;
  latitude?: number;
  longitude?: number;
}

/** Where an image the merchant supplied now lives. The URL is public and permanent. */
export const UploadedImage = z.object({
  url: z.string(),
});
export type UploadedImage = z.output<typeof UploadedImage>;
